import sha256 from 'crypto-js/sha256';
import Base64 from 'crypto-js/enc-base64';
import { isEmpty } from 'lodash';
import { CompanyType } from 'types/companiesType';
import { IdentityProviderRedirect } from 'types/sessionsTypes';
import { CALLBACK_URL } from 'constants/queryParams';
import {
  getCompaniesFromLocalStorage,
  getCodeVerifierFromLocalStorage,
  setCodeVerifierToLocalStorage,
  getUserIdTokenFromLocalStorage,
} from './storages';
import routes from 'constants/routes';

export const getHostByEnv = (host: string): string => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  if (!isDevelopment) return host;

  let redirectHost = host;

  switch (host) {
    case process.env.REACT_APP_TT_APP_DEV:
      redirectHost = process.env.REACT_APP_LOCAL_TT_APP_DEV;
      break;
    case process.env.REACT_APP_TT_APP_DEV1:
      redirectHost = process.env.REACT_APP_LOCAL_TT_APP_DEV1;
      break;
    case process.env.REACT_APP_TT_APP_DEV2:
      redirectHost = process.env.REACT_APP_LOCAL_TT_APP_DEV2;
      break;
    case process.env.REACT_APP_TT_APP_DEV3:
      redirectHost = process.env.REACT_APP_LOCAL_TT_APP_DEV3;
      break;
    case process.env.REACT_APP_TT_APP_DEV6:
      redirectHost = process.env.REACT_APP_LOCAL_TT_APP_DEV6;
      break;
    case process.env.REACT_APP_TT_APP_DEV7:
      redirectHost = process.env.REACT_APP_LOCAL_TT_APP_DEV7;
      break;
    case process.env.REACT_APP_TT_APP_DEV8:
      redirectHost = process.env.REACT_APP_LOCAL_TT_APP_DEV8;
      break;
    case process.env.REACT_APP_TT_APP_TEST:
      redirectHost = process.env.REACT_APP_LOCAL_TT_APP_TEST;
      break;
    default:
      redirectHost = host;
      break;
  }
  return redirectHost || host;
};

export const getSwitchCompanyByIndex = (
  index: string
): CompanyType | undefined => {
  const indexNumber = Number(index);
  if (isNaN(indexNumber)) return;

  const companies = getCompaniesFromLocalStorage();
  const switchCompany = companies[indexNumber];
  return switchCompany;
};

const CODE_VERIFIER_BYTES = 32;

const toBase64Url = (bytes: Uint8Array): string =>
  window
    .btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const generateCodeVerifier = (): string => {
  const cryptoObj =
    window.crypto || (window as unknown as { msCrypto?: Crypto }).msCrypto;
  if (!cryptoObj?.getRandomValues) {
    throw new Error(
      'Secure random generator unavailable: window.crypto.getRandomValues is not accessible (insecure context or unsupported browser).'
    );
  }
  const bytes = new Uint8Array(CODE_VERIFIER_BYTES);
  cryptoObj.getRandomValues(bytes);
  return toBase64Url(bytes);
};

export const generateCodeChallenge = (codeVerifier: string): string =>
  Base64.stringify(sha256(codeVerifier))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const ensureCodeVerifier = (): string => {
  let codeVerifier = getCodeVerifierFromLocalStorage();
  if (!codeVerifier) {
    codeVerifier = generateCodeVerifier();
    setCodeVerifierToLocalStorage(codeVerifier);
  }
  return codeVerifier;
};

export const buildCallbackSearch = (callbackURL?: string | null): string =>
  callbackURL ? `?${CALLBACK_URL}=${encodeURIComponent(callbackURL)}` : '';

export const pickRedirectByRegion = (
  redirects?: IdentityProviderRedirect[] | null,
  region?: string | null
): IdentityProviderRedirect | undefined => {
  const usable = (redirects || []).filter((item) => !!item?.uri);
  if (isEmpty(usable)) return undefined;

  const matched = usable.find(
    (item) => !!region && item?.region?.toUpperCase() === region.toUpperCase()
  );
  if (matched) return matched;

  if (region) {
    console.warn('No Access redirect matched the requested evo region');
  }
  return usable[0];
};

export const buildAuthorizationCallbackUrl = (search = ''): string =>
  window.location.origin +
  routes.AUTHORIZATION_CALLBACK.path +
  decodeURIComponent(search);

export const redirectToVcIdentity = (search = '') => {
  try {
    const codeVerifier = ensureCodeVerifier();
    const codeChallenge = generateCodeChallenge(codeVerifier);
    const continueUrl = encodeURIComponent(
      buildAuthorizationCallbackUrl(search)
    );
    const vcIdentityUrl =
      process.env.REACT_APP_VC_IDENTITY_URL +
      `/?code_challenge_method=S256&code_challenge=${codeChallenge}&continue=${continueUrl}`;
    window.location.replace(vcIdentityUrl);
  } catch (error) {
    redirectToVcIdentityError({
      error_code: 'code_verifier_generation_failed',
    });
  }
};

export const redirectToVcIdentityError = (params) => {
  const continueUrl = window.location.origin;
  const paramsString = Object.entries({ ...params, continue: continueUrl })
    .map((e) => e.map((v: string) => encodeURIComponent(v)).join('='))
    .join('&');
  const vcIdentityUrl =
    process.env.REACT_APP_VC_IDENTITY_URL + `/error?${paramsString}`;
  window.location.replace(vcIdentityUrl);
};

export const redirectToVcLogout = () => {
  const continueUrl = encodeURIComponent(window.location.origin);
  const vcLogoutUrl =
    process.env.REACT_APP_VC_IDENTITY_URL + `/logout?continue=${continueUrl}`;
  window.location.replace(vcLogoutUrl);
};

const getHostFromUrl = (url?: string): string | null => {
  if (!url) return null;
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return null;
  }
};

export const getAllowedIdpRedirectHosts = (): string[] =>
  [
    'amazoncognito.com',
    getHostFromUrl(process.env.REACT_APP_PUBLIC_OIDC_URL),
    getHostFromUrl(process.env.REACT_APP_VC_IDENTITY_URL),
  ].filter((host): host is string => !!host);

const matchesAllowedHost = (host: string, allowed: string): boolean =>
  host === allowed || host.endsWith(`.${allowed}`);

export const isAllowedIdpRedirectUri = (uri: string): boolean => {
  if (!uri) return false;
  try {
    const { protocol, host } = new URL(uri);
    if (protocol !== 'https:') return false;
    return getAllowedIdpRedirectHosts().some((allowed) =>
      matchesAllowedHost(host.toLowerCase(), allowed)
    );
  } catch {
    return false;
  }
};

export const redirectToAccessUrl = (uri?: string | null): boolean => {
  if (!uri) return false;
  if (!isAllowedIdpRedirectUri(uri)) {
    console.warn('Blocked Access redirect: URI is not allow-listed', uri);
    return false;
  }
  window.location.replace(uri);
  return true;
};

export const parseIdToken = (token) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );

  return JSON.parse(jsonPayload);
};

export const parseIdTokenSafely = (token: string) => {
  try {
    return parseIdToken(token);
  } catch {
    return null;
  }
};

export const isMfaEnabled = () => {
  const idToken = getUserIdTokenFromLocalStorage();
  if (!idToken) return false;

  const info = parseIdTokenSafely(idToken);
  if (!info) return false;

  let amr = info['custom:amr'];
  if (!amr) return false;

  amr = JSON.parse(amr);
  return amr.includes('otp') || amr.includes('sms');
};
